/**
 * Known issues:
 *
 * - Deletion: if user clicks to delete a word before we've received the response
 *   from the server after adding the new user word, it won't get deleted. Ideally we would
 *   keep track of all words deleted by the user in this case and then delete them once we
 *   receive the response from the server.
 */

import $ from "jquery";
import * as _ from "underscore";

import * as envelope from "envelope";
import * as objectIdUtils from "objectIdUtils";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as spanUtils from "spanUtils";
import * as vScroll from "vScroll";

type JQueryElement = JQuery<HTMLElement>;

let options: {
  updateLayout: () => void;
  sourceLang: string;
  translator: (
    // eslint-disable-next-line no-shadow
    options: Object,
    callback: (translationResponse: any) => void,
    errorCallback: () => void,
    transliterationCallback?: (transliteration: string) => void
  ) => void | Promise<void>;
  /** The container element where spans are located */
  element: JQueryElement;
  /** Whether scroll mode is enabled for mobile */
  scrollMode?: boolean;
  /** Application models containing user and userWords */
  models: {
    user: any;
    userWords: any;
  };
  speak: (language: string, text: string) => void;
  bookModel?: any;
  monolingualLookup?: (text: string, span: any, context: string) => void;
  translateDictionaryLookup: (text: UserWord, span: any) => void;
  userWordsList: any; // XXX Do we need this AND models.userWords?
  userWordCreator: (wordDefinition: Object) => any;
  deleteOnUntranslate?: boolean;
  contextBeforePage?: string;
  beforeTranslate?: () => void;
  forceReaderTranslations?: boolean;
  chooseFirstLanguage?: () => Promise<void>; // This should set the user's first language and
  // then resolve the returned promise
};

/**
 * Map of position (integer in string form) to info about the word at that position which has
 * been selected for translation by the user, either on it's own or as part of a phrase.
 */
let selectedPositions: {
  [position: string]: { span?: JQueryElement; groupId?: number };
};

let groups: { [id: string]: spanUtils.Group };
let groupCounter;
let sentPhrases;

/**
 * Hash of `${FROM-POSITION}-${TO-POSITION}` to UserWords
 */
let translatedGroups: { [key: string]: any[] };

const clickHandlers = {}; // change based on whole page

/** Callback to run when clicking on one of your highlighted words */
let yourWordClickHandler;
let dragFrom = null;
let dragTo = null;
let allSpans;
let body;

/** Position of un-translated word to delete */
let positionOfWordToDelete;

/**
 * @param {boolean} autoDelete - if true, indicates that this is being automatically deleted due to
 *                               the selected phrase being expanded
 */
const onGroupClick = async function (span, autoDelete = false) {
  const group = getGroup(span);
  if (!group) {
    return;
  }

  positionOfWordToDelete = undefined;

  _.each(selectedPositions, (info, i) => {
    if (info && info.groupId === group.id) {
      if (options.deleteOnUntranslate) {
        positionOfWordToDelete = i;
      }
      delete selectedPositions[i];
    }
  });

  const deleting = !autoDelete && tryDeletingUntranslatedWord();

  if (deleting) {
    group.span.find(".translation").css("text-decoration", "line-through");
    await utils.sleep(800);
  }

  group.span?.replaceWith(group.originalNodes.contents());

  if (options.updateLayout) {
    options.updateLayout();
  }

  delete groups[group.id];
};

// TODO: remove source lang - redundant
export const setSourceLanguage = (language) => {
  options.sourceLang = language;
};

export const setTranslateOverride = (_translator) => {
  options.translator = _translator;
};

const dragStart = (event) => {
  if (body.attr("data-spansEnabled") !== "enabled") {
    return;
  }

  event.stopPropagation();
  event.preventDefault();

  if (event.handled !== true) {
    const $target = $(event.currentTarget);

    const clickHandler = clickHandlers[$("body").attr("data-clickMode")];
    if (clickHandler) {
      clickHandler($target);
      return;
    }

    $target.addClass("selecting");
    const dragFromPosition = parseInt($target.attr("data-p"), 10);
    if (typeof dragFromPosition === "number" && !isNaN(dragFromPosition)) {
      dragFrom = { start: dragFromPosition, end: dragFromPosition };
    } else {
      const dragFromGroup = groups[parseInt($target.attr("data-group"), 10)];
      dragFrom = {
        start: dragFromGroup.from,
        end: dragFromGroup.to,
        groupSpan: dragFromGroup.span,
      };
    }
    dragTo = dragFrom.end;

    event.handled = true;
  } else {
    return false;
  }
};

/** Exported only for purposes of unit testing */
export const dragMove = (span) => {
  if (span.length === 0) {
    allSpans.removeClass("selecting");
    dragTo = null;
  } else {
    dragTo = parseInt(span.attr("data-p"), 10);

    let start;
    let end;
    if (typeof dragTo === "number" && !isNaN(dragTo)) {
      start = Math.min(dragFrom.start, dragFrom.end, dragTo);
      end = Math.max(dragFrom.start, dragFrom.end, dragTo);
    } else {
      const groupId = parseInt(span.attr("data-group"), 10);
      const group = groups[groupId];
      start = Math.min(dragFrom.start, dragFrom.end, group.from, group.to);
      end = Math.max(dragFrom.start, dragFrom.end, group.from, group.to);
    }

    let parent;
    if (dragFrom.groupSpan) {
      parent = dragFrom.groupSpan.parent();
    } else {
      parent = $('span[data-p="' + dragFrom.start + '"]').parent();
    }

    // deselect those spans not within the range
    $("span.selecting").each((index, innerSpan) => {
      const $span = $(innerSpan);
      const position = parseInt($span.attr("data-p"), 10);
      if (position < start || position > end) {
        $span.removeClass("selecting");
      }
    });

    _.each(_.range(start, end + 1), (position) => {
      const innerSpan = $('span[data-p="' + position + '"]');

      if (innerSpan.length > 0 && innerSpan.parent().is(parent)) {
        innerSpan.addClass("selecting");
      } else if (selectedPositions[position]) {
        dragTo = dragFrom.start === start ? end : start;
      } else {
        dragTo = null;
      }
    });
  }
};

const dragEnd = (span) => {
  if (dragFrom === null || dragTo === null || isNaN(dragTo)) {
    dragFrom = null;
    dragTo = null;
    allSpans.removeClass("selecting");
    return;
  }

  dragTo = dragTo || parseInt(span.attr("data-p"), 10);

  let start;
  let end;

  if (typeof dragTo === "number" && !isNaN(dragTo)) {
    start = Math.min(dragFrom.start, dragFrom.end, dragTo);
    end = Math.max(dragFrom.start, dragFrom.end, dragTo);
  } else {
    const groupId = parseInt(span.attr("data-group"), 10);
    const group = groups[groupId];
    start = Math.min(dragFrom.start, dragFrom.end, group.from, group.to);
    end = Math.max(dragFrom.start, dragFrom.end, group.from, group.to);
  }

  if (
    start === end &&
    yourWordClickHandler &&
    span.hasClass("yourWord") &&
    !span.hasClass("translated")
  ) {
    dragFrom = null;
    dragTo = null;
    allSpans.removeClass("selecting");
    const context = spanUtils.getContextSentence(
      span,
      groups,
      options.contextBeforePage
    ).text;
    yourWordClickHandler(span, context);
    return;
  }

  const createGroupMode = (() => {
    const uniqueGroupIds = _.uniq(
      _.range(start, end + 1).map(
        (position) => selectedPositions[position]?.groupId
      )
    );

    return (
      (uniqueGroupIds.length === 1 && uniqueGroupIds[0] === undefined) ||
      uniqueGroupIds.length > 1
    );
  })();

  (() => {
    // Iterate through all positions in the range
    for (let position = start; position <= end; position++) {
      if (selectedPositions[position]) {
        const group = groups[selectedPositions[position].groupId];

        position = group?.to ?? position;
        // Unselect the group or span
        if (group?.originalNodes) {
          onGroupClick(group.span, createGroupMode);
        } else {
          unTranslateSpan(
            selectedPositions[position].span,
            undefined,
            createGroupMode
          );
        }
      }
    }
  })();

  // If createGroupMode is false here, it means that we've just unselected a group. If it's true,
  // it means we need to go ahead and create a new group.
  if (createGroupMode) {
    _.each(_.range(start, end + 1), (position) => {
      selectedPositions[position] = {
        span: options.element.find('span[data-p="' + position + '"]'),
      };
    });

    createGroup(start, end);
  }
  dragFrom = null;
  dragTo = null;
  allSpans.removeClass("selecting");
};

const touchStart = (event) => {
  const touches = event.touches || event.originalEvent.touches;

  if (touches) {
    if (touches.length > 1) {
      dragFrom = null;
      dragTo = null;
      allSpans.removeClass("selecting");
      return;
    }

    if (options.scrollMode) {
      vScroll.setStart(touches[0]);
    }
  }

  dragStart(event);
};

const touchMove = (event) => {
  event = event.originalEvent;

  const pageX = event.changedTouches[0].pageX;
  const pageY = event.changedTouches[0].pageY;

  if (options.scrollMode && vScroll.setMove(event.changedTouches[0])) {
    // cancel
    dragFrom = null;
    dragTo = null;
    allSpans.removeClass("selecting");
    return;
  }

  if (dragFrom === null) {
    // can get here e.g. after multi-touch gesture
    return;
  }

  if (event.touches.length > 1) {
    // cancel
    dragFrom = null;
    dragTo = null;
    allSpans.removeClass("selecting");
    return;
  }

  const x = pageX - $(document).scrollLeft();
  const y = pageY - $(document).scrollTop();

  const span = $(document.elementFromPoint(x, y)).closest(
    "span[data-p], span[data-group]"
  );

  dragMove(span);
};
const touchEnd = (event) => {
  const pageX = event.originalEvent.changedTouches[0].pageX;
  const pageY = event.originalEvent.changedTouches[0].pageY;

  if (options.scrollMode && vScroll.setEnd()) {
    event.stopPropagation();
    event.preventDefault();

    // cancel drag
    dragFrom = null;
    dragTo = null;
    allSpans.removeClass("selecting");
    return;
  }

  if (dragFrom === null) {
    return;
  }

  event.stopPropagation();
  event.preventDefault();

  const x = pageX - $(document).scrollLeft();
  const y = pageY - $(document).scrollTop();
  const span = $(document.elementFromPoint(x, y)).closest(
    "span[data-p], span[data-group]"
  );

  dragEnd(span);
};

const mouseMove = (event) => {
  if (dragFrom === null) {
    return;
  }
  const span = $(event.target).closest("span[data-p], span[data-group]");
  dragMove(span);
};
const mouseEnd = (event) => {
  const span = $(event.target).closest("span[data-p], span[data-group]");

  if (span.length > 0) {
    dragEnd(span);
    event.stopPropagation();
    event.preventDefault();
  } else {
    dragFrom = null;
    dragTo = null;
  }
};

/**
 * Initialize span translator
 *
 * @param {typeof options} _options
 */
export const setup = (_options) => {
  options = _options;

  groupCounter = 1;
  groups = {};
  selectedPositions = {};

  if (options.scrollMode) {
    vScroll.setup({ element: options.element });
  }

  /**
   * @description hash of positions to phrases
   *
   * @type {{[position: number]: {wordCount: number, id: string}}} - id is ObjectID
   **/
  sentPhrases = {};

  translatedGroups = {};

  allSpans = options.element.find("span[data-p]");

  body = $("body");

  if ("ontouchend" in document) {
    allSpans.on("touchstart", touchStart);
    //allSpans.on('touchmove', touchMove);
    options.element.on("touchmove", touchMove);
    options.element.on("touchend", touchEnd);
    //allSpans.on('touchend', touchEnd);
  }
  allSpans.on("mousedown", dragStart);
  $(document).on("mousemove", mouseMove);
  $(document).on("mouseup", mouseEnd);

  allSpans.on("click", (event) => {
    if (body.attr("data-spansEnabled") === "enabled") {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  $("body").attr("data-spansEnabled", "enabled");
};

/**
 * Attempts to delete the context at `positionOfWordToDelete`, along with the whole word if it only
 * had this one context.
 *
 * Returns true if deletion happened.
 */
const tryDeletingUntranslatedWord = (): boolean => {
  if (positionOfWordToDelete === undefined) {
    return;
  }

  const span = getSpanFromPosition(positionOfWordToDelete);

  if (!span || span.length === 0) {
    positionOfWordToDelete = undefined;
    return;
  }

  const contextToDelete = spanUtils.getContextSentence(
    getSpanFromPosition(positionOfWordToDelete),
    groups,
    options.contextBeforePage
  );

  const sentPhrase = sentPhrases[positionOfWordToDelete];
  if (!sentPhrase) {
    // Save to try deleting again later
    return;
  }

  // XXX Having these 2 different collections of user words is messy
  const word =
    options.models.userWords.get(sentPhrase.id) ??
    options.userWordsList?.get(sentPhrase.id);

  if (!word) {
    return;
  }

  delete sentPhrases[positionOfWordToDelete];
  positionOfWordToDelete = undefined;
  if ((word.get("contexts") ?? []).length === 1) {
    // Delete the word
    word.destroy();
    return true;
  }

  if ("save" in word) {
    // Remove the context
    word.save(
      {
        contexts: word
          .get("contexts")
          .filter((context) => context.text !== contextToDelete.text),
      },
      { patch: true }
    );
    return true;
  } else if (
    // This is here only to support the Web Reader which doesn't have the `save` method.
    // Remove this if/when the Web Reader is updated to use the `save` method.
    confirm(
      `You've translated this ${
        sentPhrase.wordCount > 1 ? "phrase" : "word"
      } before in other contexts. ` + "Are you sure you want to delete it?"
    )
  ) {
    // Ask before removing the word
    word.destroy();
    return true;
  }

  return false;
};

/**
 * Remove the translation gloss from the given span
 *
 * @param {JQuery<HTMLElement>} span
 * @param {any} group
 * @param {boolean} autoDelete
 */
const unTranslateSpan = async (
  span,
  group? /* optional - the group corresponding to the span position */,
  autoDelete = false
) => {
  const originalHtml = span.attr("data-original");
  const groupId = span.attr("data-group");

  const position = getPosition(span);
  if (position) {
    group = group || getGroupFromPosition(position);
    if (group) {
      delete groups[group.id];
    } else {
      console.error("NO GROUP!");
    }
  } else {
    console.error("NO POSITION!");
  }

  if (!autoDelete && options.deleteOnUntranslate) {
    positionOfWordToDelete = position;
    const deleting = tryDeletingUntranslatedWord();
    if (deleting) {
      span.find(".translation").css("text-decoration", "line-through");
      await utils.sleep(800);
    }
  }

  if (groupId) {
    onGroupClick(span);
    return;
  }

  span.removeClass("translated translating monolingual");

  delete selectedPositions[getPosition(span)];

  if (!originalHtml) {
    return;
  }

  span.text(originalHtml);
  span.removeAttr("data-original");

  if (
    languageMap.map[options.models.user.get("currentLearningLanguage")]
      .direction !==
    languageMap.map[options.models.user.get("firstLanguage")].direction
  ) {
    span.attr(
      "dir",
      languageMap.map[options.models.user.get("currentLearningLanguage")]
        .direction || "LTR"
    );
  }

  if (options.updateLayout) {
    options.updateLayout();
  }
};

export const getGroupsFromWord = (word) =>
  _.filter(groups, (group) => {
    if (group.span.find(".original").text() === word) {
      return true;
    }
  });

export const updateTranslations = (word, translation) => {
  _.each(getGroupsFromWord(word), (group) => {
    const trans = group.span.find(".translation");

    trans.text(translation);
  });
};

const getGroupFromPosition = (position) => {
  let foundGroup;
  _.each(groups, (group) => {
    if (position >= group.from && position <= group.to) {
      if (foundGroup) {
        console.error("OVERLAPPING GROUPS!");
      } else {
        foundGroup = group;
      }
    }
  });
  return foundGroup;
};

/**
 * Returns a group span if it exists at `position`, or a normal single-word span otherwise
 */
const getSpanFromPosition = (position: number): JQueryElement => {
  const group = getGroupFromPosition(position);
  return group?.span ?? $('span[data-p="' + position + '"]');
};

var getPosition = (span) => {
  const position = span.attr("data-p");
  const toPosition = span.attr("data-to");
  if (position) {
    return parseInt(position, 10);
  } else if (toPosition) {
    return parseInt(toPosition, 10);
  } else {
    return null;
  }
};

const getGroup = (span) => {
  return spanUtils.getGroup(span, groups);
};

export const getRange = (span) => {
  const position = getPosition(span);
  if (typeof position === "number") {
    return {
      from: position,
      to: position,
    };
  } else {
    return _.pick(getGroup(span), "from", "to");
  }
};

const removeGroup = (groupId) => {
  if (groups[groupId]) {
    unTranslateSpan(groups[groupId].span, groups[groupId], true);
    delete groups[groupId];
  }
};

// direction 'next' or 'previous'
export const isBreak = (node, direction) => {
  const sibling = node[direction + "Sibling"];
  if (sibling && sibling.nodeType === 3) {
    if (/\.|!|\?/.test($(sibling).text())) {
      return true;
    } else {
      return isBreak(sibling, direction);
    }
  } else if (sibling && $(sibling).is("br")) {
    return true;
  } else {
    return false;
  }
};

const createGroup = (position, toPosition) => {
  let from = position;
  let to = toPosition || position;
  const id = groupCounter;

  selectedPositions[position].groupId = id;

  if (toPosition) {
    _.each(_.range(position, toPosition + 1), (innerPosition) => {
      selectedPositions[innerPosition].groupId = id;
    });
  }

  const parent = selectedPositions[position].span.parent();

  const groupsToRemove = {};

  if (options.models.user.get("readerMergePhrases") === "off") {
    // skip merge
  } else {
    // merge adjacent phrases
    while (
      selectedPositions[from - 1] &&
      (!groups[selectedPositions[from - 1].groupId] ||
        (!isBreak(
          groups[selectedPositions[from - 1].groupId].span[0],
          "next"
        ) &&
          groups[selectedPositions[from - 1].groupId].span
            .parent()
            .is(parent)) ||
        (selectedPositions[from - 1].span &&
          selectedPositions[from - 1].span.parent().is(parent) &&
          !isBreak(selectedPositions[from - 1].span[0], "next")))
    ) {
      from--;
      if (selectedPositions[from].groupId) {
        groupsToRemove[selectedPositions[from].groupId] = true;
      }
      selectedPositions[from] = { groupId: id };
    }
    while (
      selectedPositions[to + 1] &&
      (!groups[selectedPositions[to + 1].groupId] ||
        (!isBreak(
          groups[selectedPositions[to + 1].groupId].span[0],
          "previous"
        ) &&
          groups[selectedPositions[to + 1].groupId].span.parent().is(parent)) ||
        (selectedPositions[to + 1].span &&
          selectedPositions[to + 1].span.parent().is(parent) &&
          !isBreak(selectedPositions[to + 1].span[0], "next")))
    ) {
      to++;
      if (selectedPositions[to].groupId) {
        groupsToRemove[selectedPositions[to].groupId] = true;
      }
      selectedPositions[to] = { groupId: id };
    }
  }

  const oldSelectedPositions = _.clone(selectedPositions);
  _.each(groupsToRemove, (value, groupId) => {
    removeGroup(groupId);
  });
  selectedPositions = oldSelectedPositions;

  let span: JQueryElement;
  let originalNodes: JQueryElement;

  // Create group span
  if (from === to) {
    span = selectedPositions[position].span;
  } else {
    let dark;

    // Check if it's dark
    if (options.element.find("span[data-p=" + from + "]").hasClass("dark")) {
      dark = true;
    }

    // TODO: this is actually more complicated - need to traverse the tree
    const surroundingNodes = options.element
      .find("span[data-p=" + from + "]")
      .parent()
      .contents();

    const nodesInGroup = $("<div/>");
    let started = false;

    span = $("<span/>").attr("data-group", id);
    if (dark) {
      span.addClass("dark");
    }
    span.on("mousedown", dragStart);
    span.on("touchstart", dragStart);
    span.on("touchmove", touchMove);
    span.on("touchend", touchEnd);

    let groupText = "";

    surroundingNodes.each((_index, node) => {
      const $node = $(node);

      if (!started) {
        if (getPosition($node) === from) {
          nodesInGroup.append($node);
          groupText += $node.text();
          started = true;
        }
      } else {
        if ($node.is("br")) {
          groupText += " ";
        } else {
          groupText += $node.text();
        }
        $node.after(span);
        nodesInGroup.append($node);
        if (getPosition($node) === to) {
          return false;
        }
      }
      return;
    });

    originalNodes = nodesInGroup;

    span.text(groupText);
    span.attr("dir", "ltr");
  }

  groupCounter++;
  const group = {
    from: from,
    to: to,
    id: id,
    originalNodes: originalNodes,
    span: span,
  };
  groups[id] = group;

  translateGroup(group);
};

const rgbaRegExp = new RegExp(
  "rgba\\(([0-9\\.]+),\\s*([0-9\\.]+),\\s*([0-9\\.]+),\\s*([0-9\\.]+)\\)"
);
const rgbRegExp = new RegExp(
  "rgb\\(([0-9\\.]+),\\s*([0-9\\.]+),\\s*([0-9\\.]+)\\)"
);

// returns array of RGBA values
export const getBackgroundColor = (element) => {
  const backgroundColor = element.css("background-color");

  const rgba = rgbaRegExp.exec(backgroundColor);
  if (rgba) {
    return rgba.slice(1);
  }

  const rgb = rgbRegExp.exec(backgroundColor);
  if (rgb) {
    const result = rgb.slice(1);
    result.push("1");
    return result;
  }

  // default: assume transparent
  return [0, 0, 0, 0];
};

export const getCalculatedBackgroundColor = (element, stack) => {
  stack = stack || [];

  if (!element) {
    return composeColors(stack);
  }

  const backgroundColor = getBackgroundColor(element);
  if (backgroundColor[3] === 1) {
    stack.splice(0, 0, backgroundColor);
    return composeColors(stack);
  }

  return getCalculatedBackgroundColor(element.parent(), stack);
};

export const composeColors = (colorStack) => {
  const rgb = [255, 255, 255];
  _.each(colorStack, (rgba) => {
    rgb[0] = rgb[0] * (1 - rgba[3]) + rgba[0] * rgba[3];
    rgb[1] = rgb[1] * (1 - rgba[3]) + rgba[1] * rgba[3];
    rgb[2] = rgb[2] * (1 - rgba[3]) + rgba[2] * rgba[3];
  });
  return rgb;
};

const setTranslated = (span) => {
  span.addClass("translated");

  if (
    languageMap.map[options.models.user.get("currentLearningLanguage")]
      .direction !==
    languageMap.map[options.models.user.get("firstLanguage")].direction
  ) {
    span.attr(
      "dir",
      languageMap.map[options.models.user.get("firstLanguage")].direction ||
        "LTR"
    );
  }
};

const unTranslateAll = () => {
  $("span[data-p].translating, span[data-group].translating").each(
    (index, span) => {
      unTranslateSpan($(span), undefined, true);
    }
  );
};

const monolingualMode = () =>
  !options.forceReaderTranslations &&
  options.models.user.get("readerTranslations") === "off";

const translateGroup = async (group) => {
  const span = group.span;
  const word = span.text();

  const context = spanUtils.getContextSentence(
    span,
    groups,
    options.contextBeforePage
  );

  const data = {
    source: options.sourceLang,
    target: options.models.user.get("firstLanguage"),
    // Consolidate multiple white-space characters into one to avoid hitting phrase limit
    // incorrectly
    q: word.replace(/\s+/g, " "),
    position: span.attr("data-p"),
    bookID: options.bookModel ? options.bookModel.get("_id") : undefined,
    context: context.text,
  };

  /**
   * Punctuation characters. Make sure it's kept in sync with punctuation in `wordRegExp` in
   * spannify.js
   */
  const punctuation = '«»"”“.,?!¡¿'.split("");
  if (punctuation.includes(data.q)) {
    // In this case, user has clicked on a lone punctuation character, which we shouldn't try
    // to translate
    unTranslateSpan(span, group);
    return;
  }

  options.beforeTranslate?.();
  options.speak?.(options.sourceLang, word);

  if (options.sourceLang === options.models.user.get("firstLanguage")) {
    if (options.chooseFirstLanguage) {
      await options.chooseFirstLanguage();
    }

    data.target = options.models.user.get("firstLanguage");

    // Do this check again, only if the user has chosen an appropriate first language can we proceed
    if (options.sourceLang === options.models.user.get("firstLanguage")) {
      alert(
        "Can't translate to the same language. Please ensure that you have different " +
          "languages set for your first language and the one you are reading. " +
          "Note: Even when using Readlang in monolingual mode, you still " +
          "need these languages to be different for Readlang to generate the flashcards you will practice with " +
          "later."
      );
      return;
    }
  }

  if (span.hasClass("translating")) {
    return;
  }

  if (monolingualMode()) {
    unTranslateAll();
    span.addClass("translating translated monolingual");
  } else {
    $("span.monolingual").removeClass("translating translated monolingual");
    span.addClass("translating");
  }

  const respondToTranslate = (translationResponse) => {
    if (span.parent().length === 0) {
      // Resetting `positionOfWordToDelete` in this way could lead to a race condition
      positionOfWordToDelete = undefined;
      return;
    }
    if (!groups[group.id]) {
      // Resetting `positionOfWordToDelete` in this way could lead to a race condition
      positionOfWordToDelete = undefined;
      return;
    }
    const translation = translationResponse.translations[0];
    const alternativeTranslations = translationResponse.translations.slice(1);

    if (!monolingualMode()) {
      if (span.hasClass("translating")) {
        span.attr("data-original", span.text());
        span.html(
          '<div class="translation">' +
            [translation, ...alternativeTranslations].join(" / ") +
            "</div>" +
            '<div class="original">' +
            span.text() +
            "</div>"
        );
        setTranslated(span);
      } else {
        // don't translate if the user clicked another word
        return;
      }
    }

    const newWordDefinition: UserWord = {
      word: word,
      language: options.sourceLang,
      translation: translation,
      contexts: [
        {
          _id: objectIdUtils.makeObjectId(),
          text: context.text,
        },
      ],
    };

    if (options.bookModel && options.bookModel.has("_id")) {
      newWordDefinition.contexts[0].bookID = options.bookModel.get("_id");

      if (
        options.bookModel.has("audioURL") ||
        options.bookModel.has("youTubeID")
      ) {
        if (options.bookModel.has("youTubeID")) {
          newWordDefinition.contexts[0].youTubeID =
            options.bookModel.get("youTubeID");
        } else {
          newWordDefinition.contexts[0].audioURL =
            options.bookModel.get("audioURL");
        }
        newWordDefinition.contexts[0].audioStart = Math.max(
          0,
          envelope.getTime(options.bookModel.get("audioMap"), context.start) - 1
        );
        newWordDefinition.contexts[0].audioEnd =
          envelope.getTime(options.bookModel.get("audioMap"), context.end + 1) +
          1;
      }
      // no position for now because:
      //
      // 1. book can be edited rendering it out of date
      // 2. complicated to get position from learn page context translations
      //
      //newWordDefinition.contexts[0].position = context.start;
    }

    const newWord = options.userWordCreator(newWordDefinition);

    options.models.userWords.addWord(newWord, (addedWord) => {
      translatedGroups[group.from + "-" + group.to] = addedWord;
      addedWord.on("destroy", () => {
        delete translatedGroups[group.from + "-" + group.to];
      });

      options.translateDictionaryLookup?.(addedWord, span);
      if (monolingualMode()) {
        options.monolingualLookup?.(word, span, context.text);
      }

      const wordCount = group.to - group.from + 1;
      const toDelete = {};
      _.each(_.range(group.from, group.to + 1), (sentPosition) => {
        const sent = sentPhrases[sentPosition];

        if (sent) {
          // Can this ever not be true?
          if (sent.wordCount < wordCount) {
            toDelete[sent.id] = true;
            sentPhrases[sentPosition] = {
              wordCount: wordCount,
              id: addedWord.get("_id"),
            };
          }
        } else {
          sentPhrases[sentPosition] = {
            wordCount: wordCount,
            id: addedWord.get("_id"),
          };
        }
      });
      _.each(_.keys(toDelete), (id) => {
        let wordToDelete = options.models.userWords.get(id);
        if (wordToDelete) {
          wordToDelete.destroy();
        }
        if (options.userWordsList) {
          wordToDelete = options.userWordsList.get(id);
          if (wordToDelete) {
            wordToDelete.destroy();
          }
        }
      });

      if (options.userWordsList) {
        options.userWordsList.add(addedWord, { at: 0, merge: true });
      }

      tryDeletingUntranslatedWord();
    });
  };

  options.translator(
    data,
    respondToTranslate,
    () => {
      // Something went wrong, so un-translate
      if (group.originalNodes) {
        onGroupClick(group.span);
      } else {
        unTranslateSpan(span, group);
      }
    },
    (transliteration) => {
      // Add transliteration to the UI
      // This must be called **after** the translation callback
      if (!span.hasClass("translated")) {
        console.error("span isn't translated yet");
        return;
      }

      const translationElement = span.find(".translation");
      const translationText = translationElement.text();

      translationElement.html(
        `<div class="transliteration">${transliteration}</div><div>${translationText}</div>`
      );

      setTimeout(() => options?.updateLayout(), 100);
    }
  );
};

/** Set click handler for a specific mode (e.g. "sync" or "goto" used by SyncAudioPlayer) */
export const addClickHandler = (mode, handler) => {
  clickHandlers[mode] = handler;
};

export const setYourWordClickHandler = (handler) => {
  yourWordClickHandler = handler;
};

export const setEnabled = (enabled) => {
  if (enabled) {
    $("body").attr("data-spansEnabled", "enabled");
  } else {
    $("body").attr("data-spansEnabled", "disabled");
  }
};

export const close = () => {
  $("body").removeAttr("data-spansEnabled");
};

export const getOriginalGroupSpans = () => {
  let $spans = $();
  _.each(groups, (group) => {
    // don't understand why, but sometimes an undefined group here was causing iOS safari to silently fail,
    // hence the check
    if (group && group.originalNodes) {
      $spans = $spans.add(group.originalNodes);
    }
  });
  return $spans;
};

export const getSelectedPositions = () => selectedPositions;

export const getGroups = () => groups;

/** @param {boolean} deleteOnUntranslate */
export const setDeleteOnUntranslate = (deleteOnUntranslate) => {
  options.deleteOnUntranslate = deleteOnUntranslate;
};

export const getSentenceFromPosition = (position) => {
  const span = getSpanFromPosition(position);

  if (!span || span.length === 0) {
    throw Error(
      "TODO: Handle case were span is not found, possibly because it's within a group"
    );
  }

  const contextSentence = spanUtils.getContextSentence(
    span,
    groups,
    options.contextBeforePage,
    250
  );

  const indexOfPosition = contextSentence.nodes.findIndex((node) => {
    return (
      node.range && node.range.from <= position && position <= node.range.to
    );
  });

  return contextSentence.nodes.slice(indexOfPosition);
};
